import { jwtDecode } from 'jwt-decode'

import { APPLICATION_ROLE_TO_AUTH0_ROLE, Auth0Roles, Permission, ShipRole } from '@forto-dev/auth-sdk-models'

type PermissionTokenPayload = {
  permissions?: unknown
  impersonator?: unknown
  roles?: unknown
  ship_roles?: unknown
}

const isStringList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')

const isShipRole = (role: string): role is ShipRole => (Object.values(ShipRole) as string[]).includes(role)

/**
 * Get the permissions granted by Ship roles, as defined for the Ship roles in Auth0.
 */
export const getPermissionsFromShipRoles = (roles: ShipRole[]): Permission[] => {
  const auth0RoleNames = roles.map((role) => APPLICATION_ROLE_TO_AUTH0_ROLE.ship?.[role])
  const permissions = Object.values(Auth0Roles.SHIP)
    .filter((auth0Role) => auth0RoleNames.includes(auth0Role.name))
    .flatMap((auth0Role) => auth0Role.permissions as readonly Permission[])
  return [...new Set(permissions)]
}

/**
 * The permissions of the user come from the Auth0 `permissions` claim of the token.
 * For these tokens the permissions are derived from the Ship roles in the token instead, like auth-sdk does:
 * - tokens from the legacy Ship login have no such claim;
 * - Login As tokens carry the permissions of the Forto employee, not of the customer user;
 * - users whose Ship role is not assigned in their Auth0 organization yet (Auth0 rollout) get an empty claim.
 *
 * Temporary: remove the fallback once every token carries its permissions from Auth0 (IAM-2668).
 */
export const getPermissionsFromToken = (token: string | undefined): Permission[] => {
  if (!token) {
    return []
  }

  let payload: PermissionTokenPayload
  try {
    payload = jwtDecode<PermissionTokenPayload>(token)
  } catch {
    return []
  }

  if (isStringList(payload.permissions) && payload.permissions.length > 0 && !payload.impersonator) {
    return payload.permissions as Permission[]
  }

  const roles = isStringList(payload.roles) ? payload.roles : isStringList(payload.ship_roles) ? payload.ship_roles : []
  return getPermissionsFromShipRoles(roles.filter(isShipRole))
}
